import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, ChevronDown } from "lucide-react";

// Tabs that have a dropdown carry a `children` array of sub-links.
// Top-level links remain clickable themselves and scroll to their section.
const LINKS = [
    {
        label: "About",
        id: "about",
        children: [
            { label: "Programs", id: "programs" },
            { label: "Partners", id: "partners" },
            { label: "CEO Vision", id: "ceo" },
            { label: "Contact", id: "contact" },
        ],
    },
    { label: "Marketing", id: "marketing" },
    { label: "Media", id: "media" },
    { label: "Mentorship", id: "mentorship" },
    {
        label: "Impact",
        id: "impact",
        children: [
            { label: "Everyday Creative", id: "everyday-creative" },
            { label: "Testimonials", id: "testimonials" },
        ],
    },
    { label: "Gallery", id: "gallery" },
];

export const Navbar = ({ onApply }) => {
    const [scrolled, setScrolled] = useState(false);
    const [open, setOpen] = useState(false);
    const [openDropdown, setOpenDropdown] = useState(null);

    useEffect(() => {
        // Only fires once when component mounts; `onScroll` is defined locally
        // inside the effect and `setScrolled` is a stable React setter — no
        // outer deps needed.
        const onScroll = () => setScrolled(window.scrollY > 40);
        window.addEventListener("scroll", onScroll);
        return () => window.removeEventListener("scroll", onScroll);
    }, []);

    const go = (id) => {
        setOpen(false);
        setOpenDropdown(null);
        const el = document.getElementById(id);
        if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
    };

    return (
        <header
            data-testid="site-navbar"
            className={`fixed top-0 inset-x-0 z-50 transition-all duration-500 ${
                scrolled
                    ? "bg-black/80 backdrop-blur-xl border-b border-[#D4AF37]/15"
                    : "bg-transparent"
            }`}
        >
            <div className="max-w-7xl mx-auto px-5 sm:px-8 h-20 flex items-center justify-between">
                <button
                    data-testid="nav-logo"
                    onClick={() => go("hero")}
                    className="flex items-center gap-3 group"
                >
                    <div className="relative h-12 w-12 grid place-items-center overflow-hidden">
                        <img
                            src="/assets/young-mogul-logo.png"
                            alt="Young Mogul"
                            className="h-full w-full object-contain drop-shadow-[0_0_12px_rgba(212,175,55,0.4)] group-hover:scale-105 transition-transform"
                        />
                    </div>
                    <div className="hidden sm:flex flex-col leading-none">
                        <span className="font-display text-base tracking-wider text-white">
                            EDUTAINMENT PROS
                        </span>
                        <span className="text-[10px] uppercase tracking-[0.3em] text-[#D4AF37]">
                            Young Mogul Program
                        </span>
                    </div>
                </button>

                <nav className="hidden xl:flex flex-1 items-center justify-around mx-8">
                    {LINKS.map((l) => {
                        if (!l.children) {
                            return (
                                <button
                                    key={l.id}
                                    data-testid={`nav-link-${l.id}`}
                                    onClick={() => go(l.id)}
                                    className="text-sm 2xl:text-base uppercase tracking-[0.18em] text-white hover:text-[#D4AF37] transition-colors font-bold whitespace-nowrap"
                                >
                                    {l.label}
                                </button>
                            );
                        }
                        return (
                            <div
                                key={l.id}
                                className="relative"
                                onMouseEnter={() => setOpenDropdown(l.id)}
                                onMouseLeave={() => setOpenDropdown(null)}
                            >
                                <button
                                    data-testid={`nav-link-${l.id}`}
                                    onClick={() => go(l.id)}
                                    className="flex items-center gap-1.5 text-sm 2xl:text-base uppercase tracking-[0.18em] text-white hover:text-[#D4AF37] transition-colors font-bold whitespace-nowrap"
                                >
                                    {l.label}
                                    <ChevronDown
                                        size={14}
                                        className={`transition-transform ${
                                            openDropdown === l.id
                                                ? "rotate-180"
                                                : ""
                                        }`}
                                    />
                                </button>
                                <AnimatePresence>
                                    {openDropdown === l.id && (
                                        <motion.div
                                            initial={{ opacity: 0, y: -8 }}
                                            animate={{ opacity: 1, y: 0 }}
                                            exit={{ opacity: 0, y: -8 }}
                                            transition={{ duration: 0.18 }}
                                            data-testid={`nav-dropdown-${l.id}`}
                                            className="absolute top-full left-1/2 -translate-x-1/2 pt-3 min-w-[200px]"
                                        >
                                            <div className="bg-black/95 backdrop-blur-xl border border-[#D4AF37]/30 shadow-[0_8px_30px_rgba(212,175,55,0.15)] py-2">
                                                {l.children.map((c) => (
                                                    <button
                                                        key={c.id}
                                                        data-testid={`nav-sublink-${c.id}`}
                                                        onClick={() => go(c.id)}
                                                        className="w-full text-left px-5 py-3 text-sm uppercase tracking-[0.18em] text-white/85 hover:text-[#D4AF37] hover:bg-white/[0.04] transition-colors font-bold whitespace-nowrap"
                                                    >
                                                        {c.label}
                                                    </button>
                                                ))}
                                            </div>
                                        </motion.div>
                                    )}
                                </AnimatePresence>
                            </div>
                        );
                    })}
                </nav>

                <div className="flex items-center gap-3">
                    <button
                        data-testid="nav-apply-btn"
                        onClick={onApply}
                        className="hidden sm:inline-flex font-display tracking-widest uppercase text-xs xl:text-sm bg-[#D4AF37] text-black px-4 xl:px-5 py-2.5 xl:py-3 hover:bg-[#FFF3A1] transition-all hover:shadow-[0_0_20px_rgba(212,175,55,0.5)] whitespace-nowrap"
                    >
                        Apply Now
                    </button>
                    <button
                        data-testid="nav-mobile-toggle"
                        className="xl:hidden text-white"
                        onClick={() => setOpen(!open)}
                        aria-label="Toggle menu"
                    >
                        {open ? <X size={24} /> : <Menu size={24} />}
                    </button>
                </div>
            </div>

            <AnimatePresence>
                {open && (
                    <motion.div
                        initial={{ opacity: 0, y: -10 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -10 }}
                        className="xl:hidden bg-black/95 backdrop-blur-xl border-t border-[#D4AF37]/15"
                    >
                        <div className="px-6 py-6 flex flex-col gap-1">
                            {LINKS.map((l) => (
                                <div key={l.id}>
                                    <button
                                        data-testid={`mobile-nav-${l.id}`}
                                        onClick={() => go(l.id)}
                                        className="w-full text-left py-3 text-white/80 uppercase tracking-[0.25em] text-sm border-b border-white/5 hover:text-[#D4AF37]"
                                    >
                                        {l.label}
                                    </button>
                                    {l.children?.map((c) => (
                                        <button
                                            key={c.id}
                                            data-testid={`mobile-nav-${c.id}`}
                                            onClick={() => go(c.id)}
                                            className="w-full text-left py-2.5 pl-6 text-white/60 uppercase tracking-[0.25em] text-xs border-b border-white/5 hover:text-[#D4AF37]"
                                        >
                                            ↳ {c.label}
                                        </button>
                                    ))}
                                </div>
                            ))}
                            <button
                                data-testid="mobile-apply-btn"
                                onClick={() => {
                                    setOpen(false);
                                    onApply();
                                }}
                                className="mt-4 font-display tracking-widest uppercase text-sm bg-[#D4AF37] text-black px-6 py-3"
                            >
                                Apply Now
                            </button>
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>
        </header>
    );
};
